import { Component, TemplateRef, ViewChild, ViewContainerRef } from "@angular/core";
import { TosTableComponent } from "../../tos-table/tos-table.component";
import { ActivatedRoute, Router } from "@angular/router";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatChipsModule } from "@angular/material/chips";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  ɵFormGroupValue,
  ɵTypedOrUntyped
} from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { TableMeta } from "../../../models/table-meta.interface";
import TariffListingTableConfig from "../config/TariffListingTableConfig.json";
import { TableListRequest, Where } from "../../../models/table-list-request.interface";
import { PaginatedListDataResponse } from "../../../models/paginated-list-data-response";
import { FinanceApiService } from "../../../services/finance-a-p-iervice.service";
import { LoadingService } from "../../../services/loading.service";
import { ToasterService } from "../../../services/toaster.service";
import { RequiresPermissionDirective } from "../../../directives/evaluate-permission.directive";
import { PermissionConstants } from "../../../constants/permission-constants";
import { TARIFF_ACTIONS, TariffActions, TariffService } from "../../../services/tariff.service";
import { NgClass, NgIf } from "@angular/common";
import { from, Subject, takeUntil } from "rxjs";
import { ModalComponent } from "../../modal/modal.component";
import { DatePickerComponent } from "../../common/date-picker/date-picker.component";
import { AuditFieldsConstants } from "../../../constants/audit-fields-constants";

@Component({
  selector: "app-tariff",
  standalone: true,
  imports: [
    TosTableComponent,
    MatFormFieldModule,
    MatSelectModule,
    MatChipsModule,
    ReactiveFormsModule,
    MatIconModule,
    FormsModule,
    RequiresPermissionDirective,
    NgIf,
    DatePickerComponent,
    NgClass
  ],
  templateUrl: "./tariff.component.html",
  styleUrls: ["./tariff.component.scss"]
})
export class TariffComponent {
  tableMeta!: TableMeta;
  menuOptions = [
    {
      label: "Edit",
      value: TARIFF_ACTIONS.EDIT,
      permissionConditionSet: { permissionIds: [PermissionConstants.CHARGE.EDIT_TARIFF] }
    },
    {
      label: "View",
      value: TARIFF_ACTIONS.VIEW,
      permissionConditionSet: { permissionIds: [PermissionConstants.CHARGE.VIEW_TARIFF] }
    },
    {
      label: "Delete",
      value: TARIFF_ACTIONS.DELETE,
      permissionConditionSet: { permissionIds: [PermissionConstants.CHARGE.EDIT_TARIFF] }
    },
    {
      label: "Clone",
      value: TARIFF_ACTIONS.CLONE,
      permissionConditionSet: { permissionIds: [PermissionConstants.CHARGE.EDIT_TARIFF] }
    }
  ];
  tableListRequest: TableListRequest = {
    selectFields: [
      "id",
      "chargeId",
      "description",
      "fromDate",
      "toDate",
      "criteria",
      "priority",
      "modifiedBy.name as modifiedBy",
      "modifiedAt"
    ],
    includeTotal: true,
    orderBy: {
      ["modifiedDate"]: "desc"
    },
    limit: 15,
    page: 0
  };
  tableListData: PaginatedListDataResponse = {
    totalCount: 0,
    page: 0,
    limit: 0,
    records: []
  };
  selectedItems: any[] = [];
  chargeId!: string;
  routerNavigationAllowed?: boolean = true;
  defaultFilters: Where = { filterConditions: [], expression: "" };
  tariffCloneForm!: FormGroup;
  private modalRef: any;

  @ViewChild("tariffCloneTemplate") tariffCloneRef!: TemplateRef<any>;

  private unsubscribe$ = new Subject<void>();


  constructor(private router: Router,
              private route: ActivatedRoute,
              private financeApiService: FinanceApiService,
              private loadingService: LoadingService,
              private toasterService: ToasterService,
              private tariffService: TariffService,
              private viewContainerRef: ViewContainerRef,
              private fb: FormBuilder) {
    this.tariffCloneForm = fb.group({
      fromDate: [null, Validators.required],
      toDate: [null, Validators.required],
      description: [""]
    });
  }

  ngOnInit() {
    this.chargeId = this.route.snapshot.paramMap.get("chargeId") ?? this.tariffService.currentState.chargeId;
    this.routerNavigationAllowed = this.tariffService.currentState.routerNavigationAllowed ?? true;
    if (!this.chargeId) {
      console.error("No charge id found");
      return;
    }
    this.tableMeta = TariffListingTableConfig;
    this.setDefaultFilters();
    this.getTableList();
  }

  private setDefaultFilters() {
    // Start with the basic charge filter
    const filterConditions = [
      {
        name: "CHARGE_EQUALS",
        alias: "chargeIdEQ",
        value: [`${this.chargeId}`]
      }
    ];

    // Initial expression with only charge filter
    this.defaultFilters = {
      filterConditions: [...filterConditions],
      expression: `(chargeIdEQ)`
    };

    // Subscribe and update the filters once state is available
    this.tariffService.state$.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (state) => {
        if (state.agreementConfigurationId) {
          filterConditions.push({
            name: "AGREEMENT_CONFIG_EQUALS",
            alias: "agreementConfigEQ",
            value: [state.agreementConfigurationId]
          });
        } else {
          filterConditions.push({
            name: "WITHOUT_AGREEMENT_CONFIGURATION",
            alias: "withoutAgreementConfig",
            value: [""]
          });
        }

        // Update the defaultFilters with new conditions and expression
        this.defaultFilters = {
          filterConditions,
          expression: `(${filterConditions.map(fc => fc.alias).join(" AND ")})`
        };
      },
      error: (err) => {
        console.error("Failed to fetch agreement configuration", err);
      }
    });
  }


  getTableList() {
    this.loadingService.show("inline");
    this.tableListRequest.where = this.defaultFilters;
    this.financeApiService.listTariffs(this.tableListRequest).subscribe({
      next: (response: any) => {
        this.tableListData = response.data;
        this.loadingService.hide();
      },
      error: (error: any) => {
        this.loadingService.hide();
        this.toasterService.error(error.error.errorDesc);
      }
    });
  }

  addNewTariff() {
    this.handleTariffAction(TARIFF_ACTIONS.ADD_NEW);
    // this.tariffService.currentState.appMode = TARIFF_ACTIONS.ADD_NEW;
  }

  goBack() {
    if (!this.routerNavigationAllowed) {
      this.tariffService.emitTariffAction(TARIFF_ACTIONS.GO_BACK);
      return;
    }
    this.router.navigateByUrl("/manage-finance/charges");
  }

  handleOptionSelected(event: any) {
    const { record: tariff, actionName } = event;
    if (actionName === TARIFF_ACTIONS.EDIT) {
      this.handleTariffAction(TARIFF_ACTIONS.EDIT, tariff);
    } else if (actionName === TARIFF_ACTIONS.VIEW) {
      this.handleTariffAction(TARIFF_ACTIONS.VIEW, tariff);
    } else if (actionName === TARIFF_ACTIONS.DELETE) {
      this.deleteTariff(tariff.id);
    } else if (actionName === TARIFF_ACTIONS.CLONE) {
      this.createCloneTariffModal(tariff);
    }
  }

  private handleTariffAction(action: TariffActions, tariff?: any) {
    this.tariffService.updateState({ tariffId: tariff?.id });
    if (!this.routerNavigationAllowed) {
      this.tariffService.emitTariffAction(action);
      this.tariffService.currentState.appMode = action;
      return;
    }

    if (action === TARIFF_ACTIONS.ADD_NEW) {
      const storageCharge = this.tableListData?.records[0]?.storageCharge;

      this.router.navigate(
        [TARIFF_ACTIONS.ADD_NEW],
        {
          relativeTo: this.route,
          queryParams: storageCharge ? { showSlab: true } : {}
        }
      );
    } else {
      this.router.navigate([`${action}/${tariff.id}`], { relativeTo: this.route });
    }
  }


  private deleteTariff(id: string) {
    this.financeApiService.deleteTariff(id).subscribe({
      next: (response: any) => {
        this.toasterService.success(response.data);
        this.getTableList();
      },
      error: (error: any) => {
        this.toasterService.error(error.error.message);
      }
    });
  }

  protected readonly PermissionConstants = PermissionConstants;

  private createCloneTariffModal(record: any) {
    const modalComponentComponentRef = this.viewContainerRef.createComponent(ModalComponent);
    this.modalRef = modalComponentComponentRef;
    modalComponentComponentRef.instance.title = "Clone Tariff";
    modalComponentComponentRef.instance.width = "600px";

    this.tariffCloneForm.get('description')?.patchValue(`${record.description ?? "N/A"} - Clone`);

    function mergeTariffDataForCloning(record: any, formValue: any): any {
      const fieldsToDelete = [AuditFieldsConstants.ID, AuditFieldsConstants.CREATED_DATE, AuditFieldsConstants.MODIFIED_DATE, AuditFieldsConstants.CREATED_BY, AuditFieldsConstants.MODIFIED_BY];
      fieldsToDelete.forEach(field => delete record[field]);

      return {
        ...record,
        ...formValue,
      };

    }

    modalComponentComponentRef.instance.loadComponent(this.tariffCloneRef,{
      tariffCloneData: mergeTariffDataForCloning(record, this.tariffCloneForm.value)
    });

    modalComponentComponentRef.instance.close.subscribe((res: any) => {
      modalComponentComponentRef.destroy();
    });
  }

  closeModal() {
    if (this.modalRef) {
      this.modalRef.destroy();
    }
  }

  cloneTariff(tariffCloneData: any) {
    this.tariffCloneForm.markAllAsTouched();
    if(this.tariffCloneForm.invalid) {
      return;
    }
    this.loadingService.show()
    console.log(tariffCloneData);
    this.financeApiService.saveTariff(tariffCloneData).subscribe({
      next: (response: any) => {
        this.toasterService.success("Tariff cloned successfully.");
        this.getTableList();
        this.closeModal()
        this.loadingService.hide()
      },
      error: (error: any) => {
        this.toasterService.error(error.error.message);
        this.loadingService.hide()
      }
    })
  }

}
